import React from "react";
import { useTranslation } from "../../../localization/useTranslation";
import DotLegalPaper from "../../../common/components/dotLegalPaper/DotLegalPaper";
import { DotLegalButton, DotLegalEmptyState, DotLegalOverflowMenu, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import NoSharings from "../processingActivitySharingsTab/noSharings.svg?react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { ProcessingActivityDocumentViewModel } from "./ProcessingActivityDocumentTab.types";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import useProcessingActivityDocumentTabHooks from "./ProcessingActivityDocumentTab.hooks";
import ProcessingActivityDocumentDialog from "./processingActivityDocumentDialog/ProcessingActivityDocumentDialog";
import ProcessingActivityDeleteDocumentDialog from "./processingActivityDeleteDocumentDialog/ProcessingActivityDeleteDocumentDialog";
import DocumentPreviewLink from "../../../common/components/documentPreview/DocumentPreviewLink";
import { useDocumentPreviewManager } from "../../../common/components/documentPreview/useDocumentPreviewManager";
import { EntityType } from "../../../common/Common.types";
import TableHeaderLock from "../../../common/components/TableHeaderLock";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import AuditLogDialog from "../../../auditlogDialog/AuditLogDialog.tsx";
import { useBestPracticeDialogsHook } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.hooks.ts";
import BestPracticeCoverageResult from "@common/components/bestPracticeCheckResultDialog/BestPracticeCoverageResult.tsx";
import BestPracticeCheckDialog from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.tsx";
import BestPracticeCheckResultDialog from "@common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.tsx";
import { getAiQualityScanOverflowMenuItem } from "@common/components/bestPracticeCheckDialog/aiQualityScanOverflowMenuItem.ts";
import { DownloadAllDocumentsButton } from "@common/components/downloadAllDocuments/DownloadAllDocumentsButton";
import { useAiFeatureAccess } from "../../../plan/aiFeatureGate/useAiFeatureAccess";

export interface ProcessingActivityDocumentTabProps {
    processingActivityId?: string;
    processingActivityName?: string;
    refetch: () => void;
}

function ProcessingActivityDocumentTab(props: ProcessingActivityDocumentTabProps) {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const { showAiQualityAssurance, planActivated } = useAiFeatureAccess();
    const {
        data,
        documents,
        hasDocuments,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        isLoading,
        setSelectedDocument,
        setShowDeleteDialog,
        setShowDocumentDialog,
        showDocumentDialog,
        refetch,
        selectedDocument,
        showDeleteDialog,
        onDownloadClick,
        showAuditLogDialog,
        setShowAuditLogDialog,
    } = useProcessingActivityDocumentTabHooks(props);
    const {
        bestPracticeCheckDocument,
        setBestPracticeCheckDocument,
        bestPracticeCheckDocumentToQualityAssurance,
        setBestPracticeCheckDocumentToQualityAssurance,
        bestPracticeCheckInitialResult,
        setBestPracticeCheckInitialResult,
        onCheckComplete,
    } = useBestPracticeDialogsHook();

    const { previewableDocuments, handleDocumentClick, getPreviewHref } = useDocumentPreviewManager({
        documents: data ?? [],
        entityType: EntityType.ProcessingActivity,
        entityId: props.processingActivityId!,
        entityTitle: props.processingActivityName,
    });

    function getMenuItems(document: ProcessingActivityDocumentViewModel) {
        let menuItems = [];

        if (permissions.processingActivityPermissions.edit) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedDocument(document);
                    setShowDocumentDialog(true);
                },
            });
        }

        if (isNullOrWhitespace(document.fileName)) {
            menuItems.push({
                menuItem: translateString("openLink"),
                onClick: () => window.open(document.link!, "_blank"),
            });
        } else {
            menuItems.push({
                menuItem: translateString("download"),
                onClick: () => onDownloadClick(document),
            });
        }

        if (permissions.processingActivityPermissions.delete) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setSelectedDocument(document);
                    setShowDeleteDialog(true);
                },
            });
        }

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(document.processingActivityDocumentId);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        if (showAiQualityAssurance && !isNullOrWhitespace(document.fileName)) {
            menuItems.push(
                getAiQualityScanOverflowMenuItem(
                    () => {
                        if (document.aiQualityAssuranceResult) {
                            setBestPracticeCheckDocument({ documentId: document.documentId });
                        } else {
                            setBestPracticeCheckDocumentToQualityAssurance({ documentId: document.documentId });
                        }
                    },
                    planActivated,
                    document.isTooLargeForAi,
                    translateString
                )
            );
        }

        return menuItems;
    }

    return (
        <React.Fragment>
            <DotLegalPaper>
                {hasDocuments ? (
                    <DotLegalTableWithFilterBar
                        hasOverflowMenu
                        filterBar={{
                            isLoading: isLoading,
                            filterData: getFilterOptions(),
                            fetchFilterData: true,
                            onMenuClose: onFilterChange,
                            onRemove: onFilterChange,
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        headers={getHeaders()}
                        rowsPerPage={30}
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        data={documents}
                        clickableRows
                        emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noData")}
                        renderRow={(row, i) => (
                            <TableRow key={i} onClick={() => handleDocumentClick(row)}>
                                <TableCell>
                                    <DocumentPreviewLink previewHref={getPreviewHref(row)} onPreviewClick={() => handleDocumentClick(row)}>
                                        {row.name}
                                    </DocumentPreviewLink>
                                </TableCell>
                                <TableCell>{row.fileName ?? row.link}</TableCell>
                                <TableCell>{translateDate(row.created)}</TableCell>
                                {showAiQualityAssurance && (
                                    <TableCell>
                                        {!isNullOrWhitespace(row.fileName) && (
                                            <BestPracticeCoverageResult
                                                result={row.aiQualityAssuranceResult}
                                                isTooLargeForAi={row.isTooLargeForAi}
                                                onClick={() => {
                                                    if (row.aiQualityAssuranceResult) {
                                                        setBestPracticeCheckDocument({
                                                            documentId: row.documentId,
                                                        });
                                                    } else {
                                                        setBestPracticeCheckDocumentToQualityAssurance({
                                                            documentId: row.documentId,
                                                        });
                                                    }
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                )}
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<NoSharings />} text={translateString("noDocumentsAttachedToLegalEntity")} />
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
                    <DownloadAllDocumentsButton
                        entityType={EntityType.ProcessingActivity}
                        entityId={props.processingActivityId!}
                        show={(data?.length ?? 0) > 1}
                    />
                    {permissions.processingActivityPermissions.edit && (
                        <DotLegalButton
                            buttonType={"primary"}
                            onClick={() => {
                                setSelectedDocument(undefined);
                                setShowDocumentDialog(true);
                            }}
                        >
                            {translateString("createNew")}
                        </DotLegalButton>
                    )}
                </Box>
            </DotLegalPaper>

            {showDocumentDialog && (
                <ProcessingActivityDocumentDialog
                    onDialogClose={() => setShowDocumentDialog(false)}
                    onSave={() => {
                        refetch();
                        setShowDocumentDialog(false);
                    }}
                    selectedDocument={selectedDocument}
                />
            )}

            {showDeleteDialog && (
                <ProcessingActivityDeleteDocumentDialog
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeletedDocument={() => {
                        refetch();
                        setSelectedDocument(undefined);
                    }}
                    selectedDocument={selectedDocument!}
                    processingActivityId={props.processingActivityId!}
                />
            )}

            {!isNullOrWhitespace(showAuditLogDialog) && (
                <AuditLogDialog
                    entityType={EntityType.ProcessingActivityDocument}
                    entityId={showAuditLogDialog!}
                    entityName={(data ?? []).find((x) => x.processingActivityDocumentId === showAuditLogDialog)?.name ?? ""}
                    onDialogClose={() => setShowAuditLogDialog(null)}
                />
            )}

            {bestPracticeCheckDocumentToQualityAssurance && (
                <BestPracticeCheckDialog
                    documentId={bestPracticeCheckDocumentToQualityAssurance.documentId}
                    onDialogClose={() => {
                        setBestPracticeCheckDocumentToQualityAssurance(undefined);
                        refetch();
                    }}
                    onCheckComplete={onCheckComplete}
                />
            )}

            {bestPracticeCheckDocument && (
                <BestPracticeCheckResultDialog
                    documentId={bestPracticeCheckDocument.documentId}
                    initialResult={bestPracticeCheckInitialResult}
                    onDialogClose={() => {
                        setBestPracticeCheckDocument(undefined);
                        setBestPracticeCheckInitialResult(undefined);
                        refetch();
                    }}
                />
            )}
        </React.Fragment>
    );

    function getHeaders() {
        const headers = Array<ITableHeader<ProcessingActivityDocumentViewModel>>();
        headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "fileName", text: translateString("document"), align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
        if (showAiQualityAssurance) {
            headers.push({
                property: "aiQualityAssuranceResult",
                text: translateString("aiQualityScan"),
                extraHeaderContent: !planActivated ? <TableHeaderLock tooltip={translateString("aiAddOnPurchaseTooltip")} /> : undefined,
            });
        }
        return headers;
    }
}

export default ProcessingActivityDocumentTab;
